import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { AssetsManagementService } from './assets-management.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { finalize, map, switchMap } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzProgressModule } from 'ng-zorro-antd/progress';

export type AssetScope = 'shared' | 'defaults';
export type AssetApp = 'admin-tool' | 'platform';
export type AssetType = 'logos' | 'documents' | 'icons';
export type AssetUrlPart = '' | 'group_id' | 'customer_id' | 'vehicle_brand' | 'customer_name';

export interface UploadUrlRequest {
  scope: AssetScope;
  app: AssetApp;
  asset_type: AssetType;
  key_path: string;
  content_type: string;
}

export interface PresignedPostResponse {
  upload_url: string;
  fields: Record<string, string>;
  object_key: string,
  cdn_url: string,
  expires_at: string
}

interface AssetUploadRule {
  maxSize: number;
  contentTypes: readonly string[];
  extensions: string;
}

const ASSET_UPLOAD_RULES: Record<AssetType, AssetUploadRule> = {
  logos: {
    maxSize: 2 * 1024 * 1024,
    contentTypes: [
      'image/png',
      'image/jpeg',
      'image/svg+xml',
      'image/webp'
    ],
    extensions: '.png,.jpg,.jpeg,.svg,.webp'
  },
  documents: {
    maxSize: 10 * 1024 * 1024,
    contentTypes: [
      'application/pdf',
      'text/plain'
    ],
    extensions: '.pdf,.txt'
  },
  icons: {
    maxSize: 512 * 1024,
    contentTypes: [
      'image/png',
      'image/svg+xml'
    ],
    extensions: '.png,.svg'
  }
};

interface UploadedAsset {
  name: string;
  url: string;
  assetType: AssetType;
  contentType: string;
}

@Component({
  selector: 'assets-management',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NzButtonModule, NzIconModule, NzModalModule, NzFormModule, NzInputModule, NzSelectModule, NzProgressModule],
  templateUrl: './assets-management.component.html',
  styleUrl: './assets-management.component.scss'
})
export class AssetsManagementComponent {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  private fb = inject(FormBuilder);
  private modal = inject(NzModalService);
  private assetsService = inject(AssetsManagementService);
  private notification = inject(NzNotificationService);
  private readonly destroyRef = inject(DestroyRef);

  selectedFile: File | null = null;
  isUploading = false;
  uploadProgress = 0;
  previewUrl: string | null = null;
  uploadedAssets: UploadedAsset[] = [];

  assetForm = this.fb.nonNullable.group({
    scope: this.fb.nonNullable.control<AssetScope>('defaults', Validators.required),
    app: this.fb.nonNullable.control<AssetApp>('platform', Validators.required),
    asset_type: this.fb.nonNullable.control<AssetType>('logos', Validators.required),
    url_part: this.fb.nonNullable.control<AssetUrlPart>(''),
    key_path: this.fb.nonNullable.control('', Validators.required)
  });

  constructor() {
    this.assetForm.controls.asset_type.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onAssetTypeChanged();
      });
  }

  openAssetModal(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>): void {
    this.resetUpload();
    const modalRef: NzModalRef = this.modal.create({
      nzTitle: 'Add new asset',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzMaskClosable: false,
      nzClosable: !this.isUploading
    });

    modalRef.afterClose.subscribe(() => {
      this.resetUpload();
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      this.selectedFile = null;
      this.assetForm.controls.key_path.setValue('');
      return;
    }

    const validationError = this.validateFile(file);

    if (validationError) {
      this.notification.error(
        'Invalid file',
        validationError
      );

      input.value = '';
      this.selectedFile = null;
      this.assetForm.controls.key_path.setValue('');
      return;
    }

    this.selectedFile = file;

    this.assetForm.controls.key_path.setValue(
      file.name.toLowerCase()
    );

    const assetType = this.assetForm.controls.asset_type.value;

    if (assetType === 'logos' || assetType === 'icons') {
      this.previewUrl = URL.createObjectURL(file);
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes >= 1024 * 1024) {
      return `${bytes / (1024 * 1024)} MB`;
    }
    return `${bytes / 1024} KB`;
  }

  onAssetTypeChanged(): void {
    if (this.selectedFile && this.validateFile(this.selectedFile)) {
      this.clearPreview();
      this.selectedFile = null;
      this.assetForm.controls.key_path.setValue('');
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  private validateFile(file: File): string | null {
    const assetType =
      this.assetForm.controls.asset_type.value;

    const rule = ASSET_UPLOAD_RULES[assetType];

    if (!rule.contentTypes.includes(file.type)) {
      return `The selected file type is not valid for ${assetType}.`;
    }

    if (file.size > rule.maxSize) {
      return `Maximum size for ${assetType} is ${this.formatFileSize(rule.maxSize)
        }.`;
    }

    return null;
  }

  get canUpload(): boolean {
    return (
      this.assetForm.valid &&
      this.selectedFile !== null &&
      !this.isUploading
    );
  }

  get acceptedFileTypes(): string {
    const assetType =
      this.assetForm.controls.asset_type.value;
    return ASSET_UPLOAD_RULES[assetType].extensions;
  }

  uploadAsset(modalRef: NzModalRef): void {
    if (!this.canUpload || !this.selectedFile) {
      this.assetForm.markAllAsTouched();
      return;
    }

    const file = this.selectedFile;
    const formValue = this.assetForm.getRawValue();

    const request: UploadUrlRequest = {
      scope: formValue.scope,
      app: formValue.app,
      asset_type: formValue.asset_type,
      key_path: this.buildKeyPath(formValue.url_part, formValue.key_path),
      content_type: file.type
    };

    this.isUploading = true;
    this.uploadProgress = 0;

    this.assetsService.createUploadUrl(request).pipe(
      switchMap(response =>
        this.assetsService.uploadToS3(response, file).pipe(
          map(event => ({ event, uploadResponse: response }))
        )
      ),
      finalize(() => {
        this.isUploading = false;
      })
    ).subscribe({
      next: ({ event, uploadResponse }) => {
        if (
          event.type === HttpEventType.UploadProgress &&
          event.total
        ) {
          this.uploadProgress = Math.round(
            (event.loaded / event.total) * 100
          );
        }

        if (event.type === HttpEventType.Response) {
          this.uploadProgress = 100;

          this.uploadedAssets.unshift({
            name: file.name,
            url: uploadResponse.cdn_url,
            assetType: formValue.asset_type,
            contentType: file.type
          });

          this.notification.success(
            'Upload complete',
            'The asset was uploaded successfully.'
          );

          modalRef.close(true);
        }
      },
      error: error => {
        this.notification.error(
          'Upload failed',
          error?.error?.message ??
          'The asset could not be uploaded.'
        );
      }
    });
  }

  buildKeyPath(urlPart: AssetUrlPart, keyPath: string): string {
    const normalizedPath = keyPath.trim().replace(/\.[^/.]+$/, '');
    return urlPart ? `${urlPart}/${normalizedPath}` : normalizedPath;
  }

  private toUrlSafe(keyPath: string): string {
    return keyPath.trim().normalize('NFC').split('/').filter(Boolean)
      .map(segment => encodeURIComponent(segment.trim().toLowerCase())).join('/');
  }

  private resetUpload(): void {
    this.clearPreview();
    this.assetForm.reset({
      scope: 'defaults',
      app: 'platform',
      asset_type: 'logos',
      url_part: '',
      key_path: ''
    });

    this.selectedFile = null;
    this.isUploading = false;
    this.uploadProgress = 0;
  }

  get showPreview(): boolean {
    const assetType =
      this.assetForm.controls.asset_type.value;

    return (
      this.previewUrl !== null &&
      (assetType === 'logos' || assetType === 'icons')
    );
  }

  private clearPreview(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }

}
